import { useMachine } from "@xstate/react";
import * as React from "react";
import { useHistory, useLocation, useRouteMatch } from "react-router-dom";
import { useForm } from "react-yup";
import * as Yup from "yup";

import {
  opportunityResultStore,
  OpportunitySearchQueryState,
  SearchFormEvent,
  searchFormMachine,
  useGlobal,
  useOpportunitySearchQuery,
  useThunkDispatch,
} from "@skv/state";

import {
  AdditionalFilter,
  AsyncTypeaheadProps,
  AsyncTypeaheadSearch,
  FilterName,
  Form,
  FriendlyKeyValueUrl,
  OpportunitySearchResults,
  SvgSprite,
  useSearchFilters,
} from "@skv/core";

const SearchSchema = Yup.object({
  keyword: Yup.string().required().default("").label("Keyword"),
  where: Yup.string().required().default("").label("Where"),
  commitment: Yup.array()
    .of(Yup.number())
    .required()
    .default([])
    .label("Commitment"),
  category: Yup.array()
    .of(Yup.number())
    .required()
    .default([])
    .label("Category"),
  daytime: Yup.string().label("Day and time"),
  suitablefor: Yup.array()
    .of(Yup.number())
    .required()
    .default([])
    .label("Suitable for"),
  typeofwork: Yup.array()
    .of(Yup.number())
    .required()
    .default([])
    .label("Type of work"),
});

export const SearchFormWithFilters = (): JSX.Element => {
  const { push, replace } = useHistory();
  const formRef = React.useRef<HTMLFormElement | null>(null);
  const location = useLocation<{
    analytics?: {
      didKeywordAutoComplete: boolean;
      didWhereAutoComplete: boolean;
    };
    resetSearch?: boolean;
    searchQuery?: ReturnType<typeof useOpportunitySearchQuery>;
  }>();

  const dispatch = useThunkDispatch();
  const searchQuery = useOpportunitySearchQuery();

  const { categoryList } = useGlobal();

  const match = useRouteMatch({
    path: ["/*-volunteering", "/volunteering*"],
  });

  const initialValues = React.useMemo(
    () => ({
      keyword: "",
      where: "",
      commitment: [] as number[],
      category: [] as number[],
      suitablefor: [] as number[],
      typeofwork: [] as number[],
    }),
    []
  );

  const {
    filters,
    handleFiltersChange,
    handleSelectChange,
    handleSelectClear,
  } = useSearchFilters({
    searchQuery,
    onChange: (nextQuery) => {
      dispatch(
        opportunityResultStore.actions.fetchOpportunities(nextQuery)
      ).then((data) => {
        push(data.canonicalUrl, {
          searchQuery: nextQuery,
          analytics: location.state?.analytics,
        });
      });
    },
  });

  const [isRepeatSearch, setIsRepeatSearch] = React.useState<boolean>(() => {
    if (
      match?.isExact ||
      location.pathname.includes("/volunteering") ||
      location.search
    ) {
      return true;
    }
    return !!searchQuery.category.length;
  });

  const [openFilter, setOpenFilter] = React.useState<FilterName | null>(null);
  const [showFilters, setShowFilters] = React.useState<boolean>(isRepeatSearch);
  const [focusFilter, setFocusFilter] = React.useState<number>();

  const form = useForm({
    defaultValues: initialValues,
    validationSchema: SearchSchema,
  });

  const { setValues } = form;
  const [current, send] = useMachine(searchFormMachine);

  React.useEffect(() => {
    // If the user clicks on the Opportunities tab, or the SEEK logo,
    // pass through the resetSearch flag to reset the search form.
    if (location.state?.resetSearch) {
      setShowFilters(location.pathname === "/volunteering");
      setIsRepeatSearch(location.pathname === "/volunteering");
      setValues(() => initialValues);
      send({ type: "CLEAR_ALL_INPUTS" });
      return;
    }

    // On location change populate the WHAT input
    if (searchQuery.keyword) {
      send({ type: "KEYWORD_INPUT_CHANGE", value: searchQuery.keyword });
    } else if (searchQuery.category.length === 1) {
      send({
        type: "KEYWORD_INPUT_SELECT",
        value: categoryList.find(({ id }) => id === searchQuery.category[0]),
      });
    }

    // On location change populate the WHERE input
    if (searchQuery.where) {
      send({ type: "LOCATION_INPUT_CHANGE", value: searchQuery.where });
    }

    // On location change populate the COMMITMENT input
    if (searchQuery.commitment?.length) {
      const commitmentList = searchQuery.commitment;
      searchQuery["commitment"] = commitmentList;
      setValues((prev) => ({
        ...prev,
        commitment: commitmentList,
      }));
    }

    // On location change populate the ADDITIONAL FILTERS
    const isAdditionalFiltersOpen =
      searchQuery.category?.length ||
      searchQuery.typeofwork?.length ||
      searchQuery.suitablefor?.length;

    if (isAdditionalFiltersOpen) {
      setShowFilters(true);
      setIsRepeatSearch(true);
      setValues((prev) => ({
        ...prev,
        category: searchQuery.category || [],
        suitablefor: searchQuery.suitablefor || [],
        typeofwork: searchQuery.typeofwork || [],
      }));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const categoryOptions = React.useMemo(() => {
    return (categoryList || []).filter(
      (n) =>
        n.name
          .toLocaleLowerCase()
          .indexOf(form.values.keyword.toLocaleLowerCase()) > -1
    );
  }, [categoryList, form.values.keyword]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setShowFilters(true);
    setIsRepeatSearch(true);

    const query: OpportunitySearchQueryState = {
      ...location.state?.searchQuery,
      keyword: current.context.didKeywordAutoComplete
        ? ""
        : current.context.keyword,
      category: isRepeatSearch
        ? location.state?.searchQuery.category || []
        : current.context.category,
      commitment: isRepeatSearch
        ? location.state?.searchQuery.commitment || []
        : form.values.commitment,
      where: current.context.where,
    };

    if ("page" in query) {
      delete query.page;
    }

    const createNextHistoryState = (data: OpportunitySearchResults) => ({
      searchQuery: {
        ...query,
        where: data.canonicalLocation || query.where,
      },
      analytics: {
        didKeywordAutoComplete: current.context.didKeywordAutoComplete,
        didWhereAutoComplete: current.context.didWhereAutoComplete,
      },
    });

    /*
      push to /volunteering url to show search page
      if current url is not on the search page.
      This is needed to show the loading states,
      otherwise on a slow connection for example,
      it would just stay on the home page and only change
      page when the request is finished, and the user will
      not see the loading indicator.
    */
    if (!match) {
      push(`/volunteering`, { searchQuery: query });
      dispatch(opportunityResultStore.actions.fetchOpportunities(query)).then(
        (data) => replace(data.canonicalUrl, createNextHistoryState(data))
      );
    } else {
      dispatch(opportunityResultStore.actions.fetchOpportunities(query)).then(
        (data) => push(data.canonicalUrl, createNextHistoryState(data))
      );
    }
  };

  const onOptionSelected = (
    selected: Array<FriendlyKeyValueUrl | string>,
    field: FilterName
  ) => {
    const action: SearchFormEvent["type"] =
      field === "keyword" ? "KEYWORD_INPUT_SELECT" : "LOCATION_INPUT_SELECT";

    if (selected.length) {
      Promise.resolve()
        .then(() => {
          send({
            type: action,
            value: selected[0],
          } as SearchFormEvent);
        })
        .then(() => {
          setOpenFilter(null);
          if (field === "keyword") {
            searchQuery["keyword"] = "";
            const selectedId = (selected[0] as FriendlyKeyValueUrl).id;
            if (isRepeatSearch) {
              handleSelectChange("category", selectedId);
            } else {
              if (!searchQuery["category"].includes(selectedId)) {
                searchQuery["category"].push(selectedId);
                const prevCategories = form.getValues().category as number[];
                form.setValue(
                  "category",
                  [...prevCategories, selectedId],
                  true
                );
              }
            }
          }
          if (field === "where") {
            searchQuery[field as string] = selected[0] as string;
            if (isRepeatSearch) {
              formRef.current?.requestSubmit();
            }
          }
        });
    }
  };

  const getWhatValueFromContext = () => {
    if (current.context.keyword) {
      return current.context.keyword;
    }

    if (current.context.category.length) {
      const selectedId = current.context.category[0];
      return categoryList.find((n) => n.id === selectedId)?.name || "";
    }

    return "";
  };

  const renderMenu = React.useCallback<
    AsyncTypeaheadProps<string>["renderMenu"]
  >(
    (results, menuProps, { Menu, MenuItem, MenuHeader }) => {
      if (
        current.matches("initialSuggestions.loading") ||
        (current.matches("searchLocationsFullList.loading") &&
          current.matches("relatedSuggestions.loading"))
      ) {
        return (
          <Menu
            {...menuProps}
            ref={menuProps.ref as React.MutableRefObject<HTMLUListElement>}
            aria-labelledby="where-heading"
          >
            <MenuHeader id="where-heading">Loading please wait...</MenuHeader>
          </Menu>
        );
      }

      // Render initial suggestions if where input is < 2 chars
      // for now, we know initialSuggestions[0] is online or remote,
      if (current.context.where.length <= 2) {
        return (
          <Menu
            {...menuProps}
            ref={menuProps.ref as React.MutableRefObject<HTMLUListElement>}
            aria-labelledby="where-heading"
          >
            <MenuHeader id="remote-heading">Remote</MenuHeader>
            <MenuItem option={results[0]} position={0} />
            <MenuHeader id="where-heading">Suggested areas</MenuHeader>
            {results.slice(1).map((result, idx) => (
              <MenuItem key={result} option={result} position={idx + 1} />
            ))}
          </Menu>
        );
      }

      return (
        <Menu
          {...menuProps}
          ref={menuProps.ref as React.MutableRefObject<HTMLUListElement>}
          aria-labelledby="where-heading"
        >
          {results.length ? (
            results.map((result, idx) => (
              <MenuItem key={idx} option={result} position={idx} />
            ))
          ) : (
            <MenuHeader id="where-heading">No results</MenuHeader>
          )}
        </Menu>
      );
    },
    [current]
  );

  const commitmentList =
    filters
      .find((n) => n.name === "commitment")
      ?.data.map((n) => ({ id: `${n.id}`, name: n.name })) || [];

  return (
    <section className="search-form" aria-label="Search">
      <form.FormProvider>
        <form
          ref={formRef}
          css={`
            .form-control {
              border-color: #fff;
            }
          `}
          method="GET"
          action="/volunteering"
          onSubmit={handleSubmit}
        >
          <section className="page-banner page-banner__search py-2 py-sm-4">
            <div className="page-banner__svgs" />
            <div className="container">
              <div className="form-row align-items-end">
                <div
                  className="col-md form-group"
                  css={`
                    overflow-x: clip;
                  `}
                >
                  <label
                    className="text-white font-weight-bold"
                    htmlFor="keyword"
                  >
                    What
                  </label>
                  <AsyncTypeaheadSearch<typeof categoryOptions[0]>
                    id="keyword"
                    inputProps={{
                      name: "keyword",
                      autoComplete: "off",
                      placeholder: "What are you interested in?",
                    }}
                    labelKey={(option) => option.name}
                    onInputChange={(text) => {
                      const id = current.context.category?.[0] ?? null;
                      if (id !== null && searchQuery.category.includes(id)) {
                        // If the user has selected a category and is typing remove it from the categories list.
                        if (isRepeatSearch) {
                          handleSelectChange("category", id);
                        } else {
                          searchQuery["category"] = searchQuery.category.filter(
                            (n) => n === id
                          );
                        }
                      }

                      send({ type: "KEYWORD_INPUT_CHANGE", value: text });
                      searchQuery["keyword"] = text;
                    }}
                    onSearch={() => {
                      return;
                    }}
                    onChange={(selected) =>
                      onOptionSelected(selected, "keyword")
                    }
                    onClear={() => {
                      Promise.resolve().then(() => {
                        setOpenFilter(null);
                        send({ type: "KEYWORD_INPUT_CLEAR" });

                        const id = current.context.category?.[0] ?? null;
                        if (id !== null && searchQuery.category.includes(id)) {
                          const updatedCategories = searchQuery.category.filter(
                            (n) => n === id
                          );
                          form.setValue("category", updatedCategories, true);
                          if (isRepeatSearch) {
                            handleSelectChange("category", id);
                          } else {
                            searchQuery["category"] = updatedCategories;
                          }
                        } else {
                          searchQuery["keyword"] = "";
                          if (isRepeatSearch) {
                            formRef.current?.requestSubmit();
                          }
                        }
                      });
                    }}
                    open={
                      openFilter === "keyword" && categoryOptions.length > 0
                    }
                    onClick={() => {
                      if (openFilter !== "keyword") {
                        setOpenFilter("keyword");
                      }
                    }}
                    onFocus={() => setOpenFilter("keyword")}
                    onBlur={() => setOpenFilter(null)}
                    onKeyDown={(e) => {
                      switch (e.key) {
                        case "ArrowUp":
                        case "ArrowDown":
                          if (openFilter !== "keyword") {
                            setOpenFilter("keyword");
                            return false;
                          }
                          break;
                        case "Escape":
                          if (openFilter === "keyword") {
                            setOpenFilter(null);
                          }
                          break;
                      }
                    }}
                    isLoading={
                      current.matches("searchLocationsFullList.loading") &&
                      current.matches("relatedSuggestions.loading")
                    }
                    options={categoryOptions}
                    defaultInputValue={getWhatValueFromContext()}
                    selected={
                      current.context.keyword
                        ? []
                        : current.context.category
                            .map((id) => categoryList.find((l) => l.id === id))
                            .filter((n) => n != null)
                    }
                    minLength={0}
                    renderMenu={(
                      results,
                      menuProps,
                      { Menu, MenuItem, MenuHeader }
                    ): JSX.Element => {
                      return (
                        <Menu
                          {...menuProps}
                          ref={
                            menuProps.ref as React.MutableRefObject<HTMLUListElement>
                          }
                          aria-labelledby="category-heading"
                        >
                          <MenuHeader id="category-heading">
                            Categories
                          </MenuHeader>
                          {results.map((n, idx) => (
                            <MenuItem key={idx} option={n} position={idx} />
                          ))}
                        </Menu>
                      );
                    }}
                  />
                </div>

                <div className="col-md form-group">
                  <Form.SelectMulti
                    id="commitment-multiselect"
                    name="Commitment"
                    label="Commitment"
                    labelProps={{
                      className: "text-white font-weight-bold",
                    }}
                    className="flex-grow-1"
                    categoryTitle="Any commitment"
                    categoryType="commitments"
                    items={commitmentList}
                    defaultValue={
                      isRepeatSearch
                        ? searchQuery["commitment"].map(String)
                        : form.values.commitment.map(String)
                    }
                    onClear={() => {
                      if (isRepeatSearch) {
                        handleSelectClear("commitment");
                      } else {
                        form.setValue("commitment", [], true);
                        searchQuery["commitment"] = [];
                      }
                    }}
                    onDropdownOpen={() => setOpenFilter(null)}
                    onUpdateField={(value) => {
                      const id = Number(value);
                      if (isRepeatSearch) {
                        handleSelectChange("commitment", id);
                      } else {
                        const updatedList = form.values.commitment.includes(id)
                          ? form.values.commitment.filter((n) => n !== id)
                          : [...form.values.commitment, id];

                        form.setValue("commitment", updatedList, true);
                        searchQuery["commitment"] = updatedList;
                      }
                    }}
                  />
                </div>

                <div
                  className="col-md form-group"
                  css={`
                    overflow-x: clip;
                  `}
                >
                  <label
                    className="text-white font-weight-bold"
                    htmlFor="where"
                  >
                    Where
                  </label>
                  <AsyncTypeaheadSearch<string>
                    id="where"
                    inputProps={{
                      name: "where",
                      autoComplete: "off",
                      placeholder: "Enter suburb, town or city",
                    }}
                    open={openFilter === "where"}
                    onClick={() => {
                      if (openFilter !== "where") {
                        setOpenFilter("where");
                      }
                    }}
                    onFocus={() => setOpenFilter("where")}
                    onBlur={() => setOpenFilter(null)}
                    onInputChange={(text) => {
                      send({ type: "LOCATION_INPUT_CHANGE", value: text });
                      searchQuery["where"] = text;
                      if (openFilter !== "where") {
                        setOpenFilter("where");
                      }
                    }}
                    onSearch={(text) => {
                      if (
                        current.context.searchLocationsFullList.length === 0 &&
                        text.length > 2
                      ) {
                        send({ type: "FETCH_START_LOCATIONS", value: text });
                      }
                    }}
                    onChange={(selected) => onOptionSelected(selected, "where")}
                    onClear={() => {
                      Promise.resolve().then(() => {
                        send({ type: "LOCATION_INPUT_CLEAR" });
                        setOpenFilter(null);

                        if (isRepeatSearch) {
                          formRef.current?.requestSubmit();
                        }
                      });
                    }}
                    isLoading={
                      current.matches("searchLocationsFullList.loading") &&
                      current.matches("relatedSuggestions.loading")
                    }
                    options={(() => {
                      if (current.context.where.length <= 2) {
                        return current.context.initialSuggestions;
                      }

                      const query = current.context.where.toLowerCase();
                      if (current.context.searchLocationsFullList.length) {
                        return current.context.searchLocationsFullList.filter(
                          (n) => n.toLowerCase().includes(query)
                        );
                      }

                      return current.context.relatedSuggestions || [];
                    })()}
                    defaultInputValue={current.context.where}
                    selected={
                      current.context.where ? [current.context.where] : []
                    }
                    minLength={0}
                    onKeyDown={(e) => {
                      switch (e.key) {
                        case "Escape":
                          if (openFilter === "where") {
                            setOpenFilter(null);
                          }
                          break;
                      }
                    }}
                    renderMenu={renderMenu}
                  />
                </div>

                <div className="px-2 form-group flex-grow-1 flex-md-grow-0">
                  <button
                    type={isRepeatSearch ? "button" : "submit"}
                    className="position-relative btn btn-block btn-secondary px-4"
                    aria-label="Seek opportunities"
                    onClick={() => {
                      if (isRepeatSearch) {
                        formRef.current?.requestSubmit();
                      }
                    }}
                  >
                    SEEK
                  </button>
                </div>
              </div>
              {!showFilters && (
                <div className="form-row justify-content-end">
                  <button
                    className="text-white bg-transparent border-0 fw-bold fs-7 px-2"
                    css={`
                      line-height: 1.5;

                      &:hover {
                        text-decoration: underline;
                      }
                    `}
                    type="button"
                    aria-label="Show more options"
                    onClick={() => {
                      setFocusFilter(0);
                      setShowFilters(true);
                    }}
                  >
                    More options
                    <SvgSprite
                      id="glyph:filter"
                      width={16}
                      height={16}
                      className="ml-1"
                    />
                  </button>
                </div>
              )}
            </div>
          </section>

          {showFilters && (
            <section className="bg-secondary text-white">
              <div className="container">
                <div className="position-relative form-row flex-nowrap py-2">
                  {filters
                    .filter(
                      (n) => n.name !== "commitment" && n.name !== "daytime"
                    )
                    .map(({ data, label, name }, idx) => (
                      <AdditionalFilter
                        key={idx}
                        name={name as FilterName}
                        heading={label}
                        selectedIds={form.getValue(name) as number[]}
                        data={data}
                        shouldFocus={idx === focusFilter}
                        openFilter={openFilter}
                        setOpenFilter={setOpenFilter}
                        onChange={handleFiltersChange}
                        disabledCategory={current.context.category[0]}
                      />
                    ))}
                </div>
              </div>
            </section>
          )}
        </form>
      </form.FormProvider>
    </section>
  );
};
